import React from 'react';
import { Table, Tag, Space, Tooltip, Button, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined,
  EyeOutlined,
  CopyOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';

// Definir la interfaz para los datos de cuota
interface CuotaData {
  estado: number;
  referencia: string;
  idPlataforma: string;
  monto: number;
  fechaVencimiento: string;
  url: string;
  codBarras: string;
  codCorto: string;
  fechaActualizacionEstadoPago: string | null;
  fechaAcreditacionPago: string | null;
  numeroCuota?: number;
  fechaVencimientoOriginal?: string | null;
}

// Definir la interfaz para el objeto principal
interface ConvenioData {
  infractorId: number;
  infractor: null | string;
  usuarioId: number;
  usuario: null | string;
  porcentajeDescuento: number;
  montoTotal: number;
  estadoPago: number;
  convenioInfracciones: null | any;
  vencimiento_Cuota1: string;
  vencimiento_Cuota2: string | null;
  vencimiento_Cuota3: string | null;
  cuota1: CuotaData;
  cuota2: CuotaData;
  cuota3: CuotaData | null;
  id: null | string;
  createdAt: null | string;
  updatedAt: null | string;
  deletedAt: null | string;
  isDeleted: boolean;
}

// Definir la interfaz para el estado de cuota
interface EstadoInfo {
  estado: number;
  text: string;
  color: string;
  icon: JSX.Element;
}

// Definir la interfaz para la fila de la tabla
interface TableRowData extends CuotaData {
  numeroCuota: number;
  fechaVencimientoOriginal: string | null;
  estadoInfo: EstadoInfo;
  estadoValor: number;
}

interface TablaCuotasProps {
  data: ConvenioData;
}

const TablaCuotas: React.FC<any> = ({ data }) => {
  // Función para determinar el estado real basado en fecha de vencimiento
  const determinarEstado = (cuota: CuotaData): EstadoInfo => {
    // Si tiene fecha de acreditación, está pagado
    if (cuota.fechaAcreditacionPago) {
      return { 
        estado: 1, 
        text: 'Pagado', 
        color: 'green', 
        icon: <CheckCircleOutlined /> 
      };
    }
    
    // Si no está pagado, verificar si está vencido
    const hoy = dayjs();
    const vencimiento = dayjs(cuota.fechaVencimiento);
    
    if (vencimiento.isBefore(hoy, 'day')) {
      return { 
        estado: 2, 
        text: 'Vencido', 
        color: 'red', 
        icon: <CloseCircleOutlined /> 
      };
    }
    
    // Si no está pagado y no está vencido, está pendiente
    return { 
      estado: 0, 
      text: 'Pendiente', 
      color: 'orange', 
      icon: <ClockCircleOutlined /> 
    };
  };

  // Función para copiar URL al portapapeles
  const copiarUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success('URL copiada al portapapeles');
    } catch (err) {
      message.error('Error al copiar la URL');
    }
  };

  // Función para obtener las cuotas que no son null
  const obtenerCuotas = (): TableRowData[] => {
    const cuotas: TableRowData[] = [];
    for (let i = 1; i <= 3; i++) {
      const cuotaKey = `cuota${i}` as keyof ConvenioData;
      const cuota = data[cuotaKey] as CuotaData | null;
      
      if (cuota) {
        const estadoInfo = determinarEstado(cuota);
        
        cuotas.push({
          ...cuota,
          numeroCuota: i,
          fechaVencimientoOriginal: data[`vencimiento_Cuota${i}` as keyof ConvenioData] as string | null,
          estadoInfo: estadoInfo,
          estadoValor: estadoInfo.estado
        });
      }
    }
    return cuotas;
  };

  const cuotas = obtenerCuotas();

  // Función para renderizar el estado
  const renderEstado = (_: any, record: TableRowData) => {
    const { text, color, icon } = record.estadoInfo;
    return (
      <Tag color={color} icon={icon}>
        {text}
      </Tag>
    );
  };

  // Función para formatear fechas
  const formatearFecha = (fecha: string | null | undefined): string => {
    if (!fecha || fecha === '0001-01-01T00:00:00') return '-';
    return dayjs(fecha).format('DD/MM/YYYY HH:mm');
  };

  // Función para formatear montos
  const formatearMonto = (monto: number): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(monto);
  };

  const columns: ColumnsType<TableRowData> = [
    {
      title: 'Cuota N°',
      dataIndex: 'numeroCuota',
      key: 'numeroCuota',
      width: 80,
      align: 'center',
    },
    {
      title: 'Estado',
      key: 'estado',
      render: renderEstado,
      filters: [
        { text: 'Pendiente', value: 0 },
        { text: 'Pagado', value: 1 },
        { text: 'Vencido', value: 2 },
      ],
      onFilter: (value, record) => record.estadoValor === value,
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (monto: number) => formatearMonto(monto),
      align: 'right',
    },
    {
      title: 'Vencimiento',
      dataIndex: 'fechaVencimiento',
      key: 'fechaVencimiento',
      render: (fecha: string) => (
        <Tooltip title={`Vence: ${formatearFecha(fecha)}`}>
          <span style={{ 
            color: dayjs(fecha).isBefore(dayjs(), 'day') ? '#ff4d4f' : 'inherit',
            fontWeight: dayjs(fecha).isBefore(dayjs(), 'day') ? 'bold' : 'normal'
          }}>
            {formatearFecha(fecha)}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => dayjs(a.fechaVencimiento).unix() - dayjs(b.fechaVencimiento).unix(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'ID Plataforma',
      dataIndex: 'idPlataforma',
      key: 'idPlataforma',
      render: (id: string) => id || '-',
    },
    {
      title: 'Código Barras',
      dataIndex: 'codBarras',
      key: 'codBarras',
      render: (codigo: string) => (
        <Tooltip title={codigo}>
          <span style={{ cursor: 'help' }}>
            {codigo ? `${codigo.substring(0, 10)}...` : '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Código Corto',
      dataIndex: 'codCorto',
      key: 'codCorto',
      render: (codigo: string) => codigo || '-',
    },
    {
      title: 'Fecha Pago',
      key: 'fechaPago',
      render: (_, record: TableRowData) => (
        <>
          {record.fechaAcreditacionPago ? (
            <Tooltip title="Fecha de acreditación">
              <span style={{ color: '#52c41a' }}>
                {formatearFecha(record.fechaAcreditacionPago)}
              </span>
            </Tooltip>
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record: TableRowData) => (
        <Space size="small">
          {record.url && (
            <>
              <Tooltip title="Ver comprobante">
                <Button 
                  type="link" 
                  icon={<EyeOutlined />} 
                  onClick={() => window.open(record.url, '_blank')}
                  size="small"
                />
              </Tooltip>
              <Tooltip title="Copiar URL de pago">
                <Button 
                  type="link" 
                  icon={<CopyOutlined />} 
                  onClick={() => copiarUrl(record.url)}
                  size="small"
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Información resumen del convenio
  const ResumenConvenio: React.FC = () => {
    const cuotasPagadas = cuotas.filter(c => c.estadoValor === 1).length;
    const cuotasPendientes = cuotas.filter(c => c.estadoValor === 0).length;
    const cuotasVencidas = cuotas.filter(c => c.estadoValor === 2).length;
    const montoPagado = cuotas
      .filter(c => c.estadoValor === 1)
      .reduce((sum, c) => sum + c.monto, 0);
    const montoPendiente = cuotas
      .filter(c => c.estadoValor !== 1)
      .reduce((sum, c) => sum + c.monto, 0);

    return (
      <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
        <Space size="large" wrap>
          <div>
            <strong>Total a pagar:</strong> {formatearMonto(data.montoTotal)}
          </div>
          {data.porcentajeDescuento > 0 && (
            <div>
              <strong>Descuento:</strong> {data.porcentajeDescuento}%
            </div>
          )}
          <div>
            <Tag color="green">Pagadas: {cuotasPagadas}</Tag>
            <Tag color="orange">Pendientes: {cuotasPendientes}</Tag>
            <Tag color="red">Vencidas: {cuotasVencidas}</Tag>
          </div>
          <div>
            <strong>Monto pagado:</strong> {formatearMonto(montoPagado)}
          </div>
          <div>
            <strong style={{ color: cuotasVencidas > 0 ? '#ff4d4f' : 'inherit' }}>
              Saldo pendiente: {formatearMonto(montoPendiente)}
            </strong>
          </div>
        </Space>
      </div>
    );
  };

  return (
    <div>
      <ResumenConvenio />
      <Table
        columns={columns}
        dataSource={cuotas}
        rowKey="numeroCuota"
        pagination={false}
        bordered
        size="middle"
        summary={(pageData) => {
          if (pageData.length === 0) return null;
          
          const total = pageData.reduce((sum, record) => sum + record.monto, 0);
          const pagado = pageData
            .filter(record => record.estadoValor === 1)
            .reduce((sum, record) => sum + record.monto, 0);
          const vencido = pageData
            .filter(record => record.estadoValor === 2)
            .reduce((sum, record) => sum + record.monto, 0);
          
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <strong>Totales</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}>
                <strong>{formatearMonto(total)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} colSpan={2}>
                <Space>
                  <span>Pagado: <span style={{ color: '#52c41a' }}>{formatearMonto(pagado)}</span></span>
                  {vencido > 0 && (
                    <span>Vencido: <span style={{ color: '#ff4d4f' }}>{formatearMonto(vencido)}</span></span>
                  )}
                </Space>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} colSpan={4}>
                <strong>
                  Saldo pendiente: {' '}
                  <span style={{ color: vencido > 0 ? '#ff4d4f' : '#faad14' }}>
                    {formatearMonto(total - pagado)}
                  </span>
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </div>
  );
};

export default TablaCuotas;